import { CTAButton } from "../components/CTAButton";
import Layout from "../components/Layout";
import ScheduleTable from "../components/ScheduleTable";
import { Link, useRouter } from "@tanstack/react-router";
import {
  Award,
  CheckCircle2,
  Mail,
  MapPin,
  Phone,
  Quote,
  Shield,
  Star,
  Trophy,
  Users,
} from "lucide-react";
import { motion } from "motion/react";
import LeadForm from "../components/LeadForm";


// ─── Trust Bar ─────────────────────────────────────────────────────────────────

const trustItems = [
  {
    icon: MapPin,
    label:
      "Serving Springfield, Eugene, Thurston, Glenwood & Surrounding Areas",
  },
  { icon: Users, label: "Beginner-Friendly Classes" },
  { icon: Shield, label: "Family-Friendly Environment" },
  { icon: Award, label: "Award-Winning Academy" },
];

// ─── Programs ─────────────────────────────────────────────────────────────────

const programs = [
  {
    title: "Adult BJJ",
    icon: "🥋",
    href: "/programs/jiu-jitsu",
    description:
      "Train with purpose. Build control, adaptability, and confidence through practical no-gi grappling techniques that work in real situations.",
  },
  {
    title: "Kids BJJ",
    icon: "⭐",
    href: "/programs/kids-jiu-jitsu",
    description:
      "Help your child develop confidence, focus, and discipline in a structured, age-appropriate environment built for ages 4–10.",
  },
  {
    title: "Teens BJJ",
    icon: "🏅",
    href: "/programs/teens-jiu-jitsu",
    description:
      "Structured training for ages 11–16 that builds confidence, mental toughness, and real self-defense skills through progressive coaching.",
  },
  {
    title: "Muay Thai",
    icon: "👊",
    href: "/programs/muay-thai",
    description:
      "Improve timing, precision, and power with structured striking training rooted in the art of eight limbs.",
  },
  {
    title: "MMA",
    icon: "🥊",
    href: "/programs/mma",
    description:
      "Develop a complete skill set combining striking, wrestling, and Jiu Jitsu for a well-rounded martial arts foundation.",
  },
  {
    title: "Wrestling",
    icon: "🤼",
    href: "/programs/wrestling",
    description:
      "Build explosive takedowns, balance, and positional control to dominate on the mats and round out your grappling game.",
  },
];

// ─── Benefits ─────────────────────────────────────────────────────────────────

const benefits = [
  {
    title: "Beginner-Friendly Programs",
    desc: "Structured progression from day one — no experience required.",
  },
  {
    title: "High-Level Coaching",
    desc: "Real competition experience brought into every class.",
  },
  {
    title: "Professional Facility",
    desc: "Clean, spacious training space with dedicated mat area.",
  },
  {
    title: "Zero-Ego Culture",
    desc: "Supportive community where everyone lifts each other up.",
  },
];

// ─── Testimonials ─────────────────────────────────────────────────────────────

const testimonials = [
  {
    quote: "Great vibe, great people, and a solid program.",
    name: "Danniel R.",
  },
  {
    quote: "Professional gym with great coaches and strong structure.",
    name: "Chad H.",
  },
  {
    quote: "Extremely welcoming and high-level instruction.",
    name: "Jonathan D.",
  },
];

// ─── Service Areas ─────────────────────────────────────────────────────────────

const serviceAreas = [
  "Springfield",
  "Eugene",
  "Thurston",
  "Glenwood",
  "Fairmont",
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function HomePage() {
  const router = useRouter();

  return (
    <Layout>
      {/* ── 1. HERO + FORM ─────────────────────────────────────────────── */}
      <section
        id="hero"
        className="relative bg-hero overflow-hidden min-h-[580px] md:min-h-[640px]"
        aria-label="Hero section"
      >
        <video src="/assets/video/mainstage-video.mp4"
          autoPlay
          muted
          loop
          playsInline className="absolute inset-0 bg-cover bg-center" aria-hidden="true" poster="/assets/generated/hero-martial-arts.dim_1600x900.jpg" style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }} />
        {/* Background image with overlay */}
        <div className="absolute inset-0 bg-gradient-hero" aria-hidden="true" />
        <div className="absolute inset-0 bg-hero/60" aria-hidden="true" />

        {/* Gold top accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />

        <div className="relative container mx-auto px-4 sm:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left: Headline + CTAs */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="flex flex-col gap-6"
            >
              <div>
                <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3 border border-gold/40 px-3 py-1 rounded-full">
                  Springfield's #1 Martial Arts Academy
                </span>
                <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight text-balance">
                  Jiu Jitsu for Kids, Teens &amp; Adults in Springfield, OR
                </h1>
              </div>
              <p className="text-base sm:text-lg text-hero-foreground leading-relaxed max-w-lg font-body">
                Build confidence, get in shape, and learn real self-defense with
                expert coaching in a supportive, ego-free environment.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <CTAButton
                  variant="hero"
                  size="lg"
                  onClick={() => router.navigate({ to: "/contact-us" })}
                  data-ocid="hero-cta-primary"
                >
                  Claim Your Free Class
                </CTAButton>
                <CTAButton
                  variant="outline"
                  size="lg"
                  onClick={() => router.navigate({ to: "/schedule" })}
                  data-ocid="hero-cta-secondary"
                >
                  View Schedule
                </CTAButton>
              </div>

              {/* Quick stats */}
              <div className="flex gap-6 pt-2">
                {[
                  ["10+", "Years"],
                  ["500+", "Students"],
                  ["5★", "Rated"],
                ].map(([val, lbl]) => (
                  <div key={lbl} className="flex flex-col">
                    <span className="font-display font-black text-xl text-gold">
                      {val}
                    </span>
                    <span className="text-xs text-hero-foreground opacity-60 font-body">
                      {lbl}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right: Contact Form */}
            <motion.div
              id="contact"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              className="bg-primary/80 backdrop-blur-sm border border-gold/30 rounded-2xl p-6 sm:p-8 shadow-elevated relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />
              <h4 className="font-display font-bold text-lg text-hero-foreground leading-snug pb-4">
                Get Started Today!{" "}
                <span className="text-gold">
                  No experience needed. Beginners welcome.
                </span>
              </h4>
              <LeadForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 2. TRUST BAR ────────────────────────────────────────────────── */}
      <section
        className="bg-card border-y border-border shadow-card"
        aria-label="Trust indicators"
      >
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
            {trustItems.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2 text-sm font-body font-medium text-muted-foreground"
              >
                <Icon className="w-4 h-4 text-gold flex-shrink-0" />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. WHY TRAIN HERE ───────────────────────────────────────────── */}
      <section
        id="about"
        className="bg-background py-20"
        aria-labelledby="why-heading"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center mb-12"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3">
              Why Choose Us
            </span>
            <h2
              id="why-heading"
              className="font-display font-black text-3xl sm:text-4xl text-foreground mb-6 text-balance"
            >
              Why Choose Superhero Jiu Jitsu Academy in Springfield, OR
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-4">
              Superhero Jiu Jitsu Academy delivers more than martial arts
              training. Every class is designed to help you build confidence,
              discipline, and real-world skills in a structured, supportive
              environment.
            </p>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              You will train alongside people of all experience levels — from
              complete beginners to active competitors. Coaching focuses on
              practical application, helping you improve fitness, mental
              resilience, and self-defense skills that carry into everyday life.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="bg-card rounded-xl p-6 shadow-card border border-border flex flex-col gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-foreground" />
                </div>
                <h3 className="font-display font-bold text-base text-foreground">
                  {b.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {b.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. PROGRAMS ─────────────────────────────────────────────────── */}
      <section
        id="programs"
        className="bg-muted/30 py-20"
        aria-labelledby="programs-heading"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3">
              What We Offer
            </span>
            <h2
              id="programs-heading"
              className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance"
            >
              Martial Arts Programs in Springfield, OR
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {programs.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="bg-card rounded-xl p-6 shadow-card border border-border flex flex-col gap-4 hover:shadow-elevated transition-smooth group"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold text-xl flex-shrink-0">
                  {p.icon}
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg text-foreground mb-2">
                    {p.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {p.description}
                  </p>
                </div>
                <div className="mt-auto pt-2">
                  <Link
                    to={p.href}
                    className="text-sm font-semibold text-gold group-hover:underline hover:underline cursor-pointer"
                    aria-label={`Learn more about ${p.title}`}
                  >
                    Learn more →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="flex justify-center">
            <CTAButton
              variant="secondary"
              size="lg"
              onClick={() => router.navigate({ to: "/programs" })}
              data-ocid="programs-cta"
            >
              Explore All Programs
            </CTAButton>
          </div>
        </div>
      </section>

      {/* ── 5. BEGINNER FRIENDLY ─────────────────────────────────────────── */}
      <section
        className="bg-background py-20"
        aria-labelledby="beginner-heading"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-card rounded-2xl shadow-elevated border border-border overflow-hidden"
            >
              <div className="h-2 bg-gradient-accent" />
              <div className="p-8 sm:p-12 flex flex-col lg:flex-row gap-8 items-center">
                <div className="flex-1">
                  <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3">
                    No Experience Needed
                  </span>
                  <h2
                    id="beginner-heading"
                    className="font-display font-black text-3xl sm:text-4xl text-foreground mb-4 text-balance"
                  >
                    New to Martial Arts? Start Here.
                  </h2>
                  <p className="text-muted-foreground text-base leading-relaxed mb-4">
                    Starting something new can feel intimidating. At Superhero
                    Jiu Jitsu Academy, beginners are welcomed and supported from
                    day one.
                  </p>
                  <p className="text-muted-foreground text-base leading-relaxed mb-6">
                    There is no need to be in shape or have prior experience.
                    Every class is designed to help you learn step by step,
                    build confidence, and improve at your own pace.
                  </p>
                  <CTAButton
                    variant="hero"
                    size="lg"
                    onClick={() => router.navigate({ to: "/contact-us" })}
                    data-ocid="beginner-cta"
                  >
                    Try Your First Class Free
                  </CTAButton>
                </div>

                <div className="lg:w-56 flex flex-col gap-3 flex-shrink-0">
                  {[
                    "No prior fitness required",
                    "Step-by-step instruction",
                    "Supportive teammates",
                    "Learn at your own pace",
                    "Kids, teens & adults welcome",
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 text-sm font-body text-foreground"
                    >
                      <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 6. ABOUT / AUTHORITY ─────────────────────────────────────────── */}
      <section
        id="about"
        className="bg-hero py-20"
        aria-labelledby="about-heading"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex flex-col lg:flex-row gap-10 items-center"
            >
              {/* Trophy icon / visual */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 rounded-full bg-gradient-accent flex items-center justify-center shadow-gold">
                  <Trophy className="w-16 h-16 text-foreground" />
                </div>
              </div>

              <div className="flex-1 text-center lg:text-left">
                <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3">
                  Our Story
                </span>
                <h2
                  id="about-heading"
                  className="font-display font-black text-3xl sm:text-4xl text-hero-foreground mb-4 text-balance"
                >
                  Serving Springfield Since 2014
                </h2>
                <p className="text-hero-foreground text-base leading-relaxed mb-4">
                  Founded by Mike DeWitt Jr., Superhero Jiu Jitsu Academy has
                  become one of the most established training facilities in Lane
                  County.
                </p>
                <p className="text-hero-foreground text-base leading-relaxed mb-6">
                  With a background in wrestling and high-level Jiu Jitsu
                  competition, Mike brings real-world experience into every
                  class. Students benefit from coaching that emphasizes
                  technique, consistency, and performance under pressure.
                </p>
                <CTAButton
                  variant="outline"
                  size="md"
                  onClick={() => router.navigate({ to: "/contact-us" })}
                  data-ocid="about-cta"
                >
                  Train With Us
                </CTAButton>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 7. TESTIMONIALS ──────────────────────────────────────────────── */}
      <section
        id="reviews"
        className="bg-muted/30 py-20"
        aria-labelledby="testimonials-heading"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3">
              Student Reviews
            </span>
            <h2
              id="testimonials-heading"
              className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance"
            >
              What Students Are Saying
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="bg-card rounded-xl p-6 shadow-card border border-border flex flex-col gap-4"
              >
                {/* Stars */}
                <div className="flex gap-1">
                  {["s1", "s2", "s3", "s4", "s5"].map((k) => (
                    <Star key={k} className="w-4 h-4 text-gold fill-current" />
                  ))}
                </div>
                <div className="relative flex-1">
                  <Quote
                    className="w-6 h-6 text-gold/30 absolute -top-1 -left-1"
                    aria-hidden="true"
                  />
                  <p className="text-sm text-muted-foreground leading-relaxed pl-4 italic">
                    "{t.quote}"
                  </p>
                </div>
                <div className="flex items-center gap-3 pt-2 border-t border-border">
                  <div className="w-8 h-8 rounded-full bg-gradient-accent flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-display font-bold text-foreground">
                      {t.name.charAt(0)}
                    </span>
                  </div>
                  <span className="font-display font-semibold text-sm text-foreground">
                    {t.name}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8. OFFER CTA BANNER ──────────────────────────────────────────── */}
      <section
        className="bg-hero relative overflow-hidden py-20"
        aria-labelledby="offer-heading"
      >
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-5" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, var(--color-accent) 0, var(--color-accent) 1px, transparent 0, transparent 50%)",
              backgroundSize: "20px 20px",
            }}
          />
        </div>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-accent" />

        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
              Limited Availability
            </span>
            <h2
              id="offer-heading"
              className="font-display font-black text-4xl sm:text-5xl text-hero-foreground mb-4 text-balance"
            >
              Claim Your Free Class
            </h2>
            <p className="text-hero-foreground text-lg mb-8 leading-relaxed">
              Take the first step toward better fitness, confidence, and
              self-defense. No experience or commitment required.
            </p>
            <CTAButton
              variant="hero"
              size="xl"
              onClick={() => router.navigate({ to: "/contact-us" })}
              data-ocid="offer-cta"
            >
              Book Your Free Class
            </CTAButton>
          </motion.div>
        </div>
      </section>

      {/* ── 9. SCHEDULE ──────────────────────────────────────────────────── */}
      <section
        id="schedule"
        className="bg-muted/30 py-20"
        aria-labelledby="schedule-heading"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3">
              Weekly Classes
            </span>
            <h2
              id="schedule-heading"
              className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance"
            >
              Class Schedule
            </h2>
            <p className="text-muted-foreground mt-3 text-base max-w-xl mx-auto">
              Monday through Saturday — find the perfect class for your
              schedule.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ScheduleTable />
          </motion.div>

          <div className="flex justify-center mt-8">
            <CTAButton
              variant="hero"
              size="lg"
              onClick={() => router.navigate({ to: "/contact-us" })}
              data-ocid="schedule-cta"
            >
              Reserve Your Free Class
            </CTAButton>
          </div>
        </div>
      </section>

      {/* ── 10. LOCATION ─────────────────────────────────────────────────── */}
      <section
        id="location"
        className="bg-background py-20"
        aria-labelledby="location-heading"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3">
                Find Us
              </span>
              <h2
                id="location-heading"
                className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance"
              >
                Jiu Jitsu Near Springfield, Eugene &amp; Surrounding Areas
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* NAP */}
              <div className="bg-card rounded-xl p-8 shadow-card border border-border">
                <h3 className="font-display font-bold text-lg text-foreground mb-6">
                  Academy Location
                </h3>
                <div className="flex flex-col gap-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                    <address className="not-italic text-sm text-muted-foreground leading-relaxed">
                      1485 Mohawk Blvd
                      <br />
                      Springfield, OR 97477
                    </address>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gold flex-shrink-0" />
                    <a
                      href="tel:+15416003371"
                      className="text-sm text-foreground hover:text-gold transition-colors-fast font-medium"
                    >
                      (541) 600-3371
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gold flex-shrink-0" />
                    <a
                      href="mailto:shjjoffice@gmail.com"
                      className="text-sm text-foreground hover:text-gold transition-colors-fast font-medium break-all"
                    >
                      shjjoffice@gmail.com
                    </a>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-border">
                  <CTAButton
                    variant="hero"
                    size="md"
                    fullWidth
                    onClick={() => router.navigate({ to: "/contact-us" })}
                    data-ocid="location-cta"
                  >
                    Book Your Free Class
                  </CTAButton>
                </div>
              </div>

              {/* Service areas */}
              <div className="bg-card rounded-xl p-8 shadow-card border border-border">
                <h3 className="font-display font-bold text-lg text-foreground mb-4">
                  We Serve
                </h3>
                <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
                  Athletes from across the region come to Superhero Jiu Jitsu
                  Academy for top-tier instruction:
                </p>
                <ul className="flex flex-col gap-2">
                  {serviceAreas.map((area) => (
                    <li
                      key={area}
                      className="flex items-center gap-3 text-sm font-body text-foreground"
                    >
                      <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
                      <span>{area}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground mt-4">
                  …and all surrounding communities in Lane County.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
